// WindAndRainData.js
import React from "react";
import {
  Typography,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AirIcon from "@mui/icons-material/Air";
import WaterIcon from "@mui/icons-material/Water";
import NavigationIcon from "@mui/icons-material/Navigation";
import { getWindDirectionText } from "./utils/weatherIcons";
import { useUnits } from "../../contexts/UnitContext";

const WindAndRainData = ({
  windData,
  rainData,
  lastUpdated,
  selectedDate,
  setSelectedDate,
}) => {
  const theme = useTheme();
  const { unitSystem, convert } = useUnits();

  // Format wind speed based on unit preference
  const formatWindSpeed = (metersPerSecond) => {
    if (unitSystem === 'metric') {
      // Keep as m/s for metric
      return `${metersPerSecond.toFixed(1)} m/s`;
    } else {
      // Convert to mph for imperial
      const mph = metersPerSecond * 2.23694;
      return `${mph.toFixed(1)} mph`;
    }
  };

  // Format rain amount based on unit preference
  const formatRainAmount = (millimeters) => {
    if (unitSystem === 'metric') {
      // Keep as mm for metric
      return `${millimeters} mm`;
    } else {
      // Convert to inches for imperial
      const inches = millimeters * 0.0393701;
      return `${inches.toFixed(2)} in`;
    }
  };

  // Process data to organize by day
  const windAndRainDataByDay = {};
  if (windData && rainData) {
    windData.forEach((windHour, index) => {
      const rainHour = rainData[index];
      if (rainHour) {
        // Use convert.time.formatDate to get consistent date formatting
        const date = convert.time.formatDate(new Date(windHour.dt * 1000));
        if (!windAndRainDataByDay[date]) {
          windAndRainDataByDay[date] = [];
        }
        windAndRainDataByDay[date].push({ wind: windHour, rain: rainHour });
      }
    });
  }

  const availableDates = Object.keys(windAndRainDataByDay);
  const selectedDateData = selectedDate
    ? windAndRainDataByDay[selectedDate]
    : null;

  if (!windData || !rainData) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" color="text.secondary">
          No wind and rain data available.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Chip
          icon={<AccessTimeIcon fontSize="small" />}
          label={`Last Updated: ${convert.time.formatDate(lastUpdated, true)}`}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Box>

      <FormControl
        fullWidth
        variant="outlined"
        size="small"
        sx={{
          mb: 3,
          maxWidth: 400,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
      >
        <InputLabel id="date-select-label">Select Date</InputLabel>
        <Select
          labelId="date-select-label"
          id="date-select"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          label="Select Date"
        >
          <MenuItem value="">
            <em>Select a Date</em>
          </MenuItem>
          {availableDates.map((date) => (
            <MenuItem key={date} value={date}>
              {date}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedDateData ? (
        <Box sx={{ overflowX: "auto" }}>
          <Grid
            container
            spacing={2}
            sx={{ minWidth: Math.min(selectedDateData.length * 180, 1200) }}
          >
            {selectedDateData.map(({ wind, rain }, index) => {
              // Use convert.time.format for consistent time formatting
              const time = convert.time.format(new Date(wind.dt * 1000));
              const windSpeed = wind.wind_speed;
              const windDegree = wind.wind_deg;
              const rainAmount = rain.rain ? rain.rain : 0;

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Card
                    elevation={1}
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        sx={{
                          
                          color: theme.palette.text.primary,
                          p: 1,
                          borderRadius: 1,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ mr: 1, color: theme.palette.text.primary }}
                        />
                        <Typography variant="subtitle2" fontWeight="medium">
                          {time}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <AirIcon
                              fontSize="small"
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Wind
                            </Typography>
                          </Box>
                          <Chip
                            label={formatWindSpeed(windSpeed)}
                            size="small"
                            sx={{
                              fontWeight: "medium",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mt: 1,
                          }}
                        >
                          <NavigationIcon
                            sx={{
                              transform: `rotate(${windDegree}deg)`,
                              color: theme.palette.primary.main,
                              mr: 1,
                            }}
                          />
                          <Typography variant="body2" fontWeight="medium">
                            {getWindDirectionText(windDegree)} ({windDegree}°)
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <WaterIcon
                              fontSize="small"
                              sx={{ mr: 1, color: theme.palette.info.main }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Rain
                            </Typography>
                          </Box>
                          <Chip
                            label={formatRainAmount(rainAmount)}
                            size="small"
                            color={rainAmount > 0 ? "info" : "default"}
                            sx={{
                              fontWeight: "medium",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            bgcolor: theme.palette.grey[200],
                            borderRadius: 1,
                            mt: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.min(rainAmount * 10, 100)}%`,
                              bgcolor: theme.palette.info.main,
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: 2,
            bgcolor: theme.palette.grey[50],
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          {selectedDate ? (
            <Typography variant="body1" color="text.secondary">
              No data available for selected date.
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Please select a date to view wind and rain data.
            </Typography>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default WindAndRainData;
